import { apiService } from './apiService';

export interface HeatmapPoint {
  id: string;
  latitude: number;
  longitude: number;
  address?: string;
  intensity: number;
  type: 'report' | 'assignment' | 'escalation' | 'response_time';
  timestamp: string;
  metadata: {
    departmentId?: string;
    departmentName?: string;
    categoryId?: string;
    priority?: string;
    status?: string;
    responseTime?: number;
    [key: string]: any;
  };
}

export interface ClusteredPoint {
  id: string;
  latitude: number;
  longitude: number;
  pointCount: number;
  pointIds: string[];
  departmentId?: string;
  averageResponseTime?: number;
  averagePriority?: number;
  types: string[];
}

export interface ServiceArea {
  id: string;
  name: string;
  departmentId: string;
  departmentName: string;
  boundary: Array<{ latitude: number; longitude: number }>;
  areaKm2: number;
  technicianCount: number;
  averageResponseTime: number;
  totalReports: number;
  averagePriority: number;
}

export interface GISFilters {
  departmentId?: string;
  categoryId?: string;
  eventType?: 'report' | 'assignment' | 'escalation' | 'response_time';
  startDate?: string;
  endDate?: string;
  radiusKm?: number;
  zoomLevel?: number;
  status?: string;
  priority?: string;
}

export interface GISStats {
  totalPoints: number;
  averageResponseTime: number;
  totalReports: number;
  totalAssignments: number;
  coveragePercentage: number;
  departmentBreakdown: Array<{
    departmentId: string;
    departmentName: string;
    pointCount: number;
    averageResponseTime: number;
  }>;
}

class GISService {
  private baseUrl = '/api/v1';

  // Get heatmap data for a specific area
  async getHeatmapData(filters: GISFilters, departments: Array<{ id: string; name: string }> = []): Promise<HeatmapPoint[]> {
    try {
      const params = new URLSearchParams();

      if (filters.departmentId) params.append('department_id', filters.departmentId);
      if (filters.categoryId) params.append('category_id', filters.categoryId);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.radiusKm) params.append('radius', filters.radiusKm.toString());
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);

      const response = await apiService.get(`/gis/heatmap?${params.toString()}`);

      if (response.success) {
        return response.data.map((point: any) => ({
          id: point.id,
          latitude: parseFloat(point.latitude),
          longitude: parseFloat(point.longitude),
          address: point.address,
          intensity: parseFloat(point.intensity),
          type: point.type,
          timestamp: point.timestamp,
          metadata: point.metadata || {},
        }));
      }

      return [];
    } catch (error) {
      console.error('Error fetching heatmap data:', error);
      return this.generateMockHeatmapData(filters, departments);
    }
  }

  // Get clustered points for efficient map rendering
  async getClusteredPoints(filters: GISFilters): Promise<ClusteredPoint[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters.departmentId) params.append('department_id', filters.departmentId);
      if (filters.zoomLevel) params.append('zoom', filters.zoomLevel.toString());
      if (filters.radiusKm) params.append('radius', filters.radiusKm.toString());

      const response = await apiService.get(`/gis/clusters?${params.toString()}`);
      
      if (response.success) {
        return response.data.map((cluster: any) => ({
          id: cluster.id,
          latitude: parseFloat(cluster.latitude),
          longitude: parseFloat(cluster.longitude),
          pointCount: cluster.point_count,
          pointIds: cluster.point_ids || [],
          departmentId: cluster.department_id,
          averageResponseTime: cluster.average_response_time,
          averagePriority: cluster.average_priority,
          types: cluster.types || [],
        }));
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching clustered points:', error);
      return this.generateMockClusteredData(filters);
    }
  }

  // Get service area boundaries
  async getServiceAreas(filters: GISFilters): Promise<ServiceArea[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters.departmentId) params.append('department_id', filters.departmentId);

      const response = await apiService.get(`/gis/service-areas?${params.toString()}`);
      
      if (response.success) {
        return response.data.map((area: any) => ({
          id: area.id,
          name: area.name,
          departmentId: area.department_id,
          departmentName: area.department_name,
          boundary: area.boundary || [],
          areaKm2: parseFloat(area.area_km2) || 0,
          technicianCount: area.technician_count || 0,
          averageResponseTime: parseFloat(area.average_response_time) || 0,
          totalReports: area.total_reports || 0,
          averagePriority: parseFloat(area.average_priority) || 0,
        }));
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching service areas:', error);
      return this.generateMockServiceAreas(filters);
    }
  }

  // Get GIS statistics and analytics. Deliberately does not fall back to
  // mock data on failure -- this used to silently return Math.random()
  // numbers whenever the endpoint failed (which was always, since the
  // endpoint didn't exist), showing fabricated metrics with no indication
  // to the viewer they weren't real. A real failure should surface as a
  // real error, same as the page's other data (heatmap/clusters/service
  // areas) already does.
  async getGISStats(filters: GISFilters): Promise<GISStats> {
    const params = new URLSearchParams();

    if (filters.departmentId) params.append('department_id', filters.departmentId);
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);

    const response = await apiService.get(`/gis/stats?${params.toString()}`);

    if (!response.success) {
      throw new Error('Failed to load GIS stats');
    }
    return response.data;
  }

  // Calculate distance between two points (Haversine formula)
  calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
               Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
               Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    
    return R * c;
  }

  // Check if a point is within a polygon (ray casting algorithm)
  isPointInPolygon(point: { latitude: number; longitude: number }, polygon: Array<{ latitude: number; longitude: number }>): boolean {
    let inside = false;
    let j = polygon.length - 1;
    
    for (let i = 0; i < polygon.length; i++) {
      if (((polygon[i].latitude > point.latitude) !== (polygon[j].latitude > point.latitude)) &&
          (point.longitude < (polygon[j].longitude - polygon[i].longitude) * 
           (point.latitude - polygon[i].latitude) / 
           (polygon[j].latitude - polygon[i].latitude) + polygon[i].longitude)) {
        inside = !inside;
      }
      j = i;
    }
    
    return inside;
  }

  // Generate optimal route between multiple points (nearest neighbor)
  getOptimalRoute(points: Array<{ latitude: number; longitude: number }>): Array<{ latitude: number; longitude: number }> {
    if (points.length <= 2) return points;
    
    const route = [points[0]];
    const unvisited = points.slice(1);
    
    while (unvisited.length > 0) {
      const current = route[route.length - 1];
      let nearestIndex = 0;
      let nearestDistance = Infinity;
      
      for (let i = 0; i < unvisited.length; i++) {
        const distance = this.calculateDistance(
          current.latitude, current.longitude,
          unvisited[i].latitude, unvisited[i].longitude
        );
        
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = i;
        }
      }
      
      route.push(unvisited[nearestIndex]);
      unvisited.splice(nearestIndex, 1);
    }
    
    return route;
  }

  // Calculate service area coverage
  calculateServiceCoverage(areas: ServiceArea[], technicianLocations: Array<{ latitude: number; longitude: number }>): Map<string, number> {
    const coverage = new Map<string, number>();
    
    for (const area of areas) {
      let coveredPoints = 0;
      const totalPoints = area.boundary.length;
      
      for (const techLocation of technicianLocations) {
        if (this.isPointInPolygon(techLocation, area.boundary)) {
          coveredPoints++;
        }
      }
      
      coverage.set(area.id, totalPoints > 0 ? coveredPoints / totalPoints : 0);
    }
    
    return coverage;
  }

  // Generate heatmap intensity based on point density
  calculateHeatmapIntensity(point: { latitude: number; longitude: number }, dataPoints: HeatmapPoint[], radiusKm: number): number {
    let totalIntensity = 0;
    
    for (const dataPoint of dataPoints) {
      const distance = this.calculateDistance(
        point.latitude, point.longitude,
        dataPoint.latitude, dataPoint.longitude
      );
      
      if (distance <= radiusKm) {
        // Inverse distance weighting
        const weight = 1 / (1 + distance);
        totalIntensity += dataPoint.intensity * weight;
      }
    }
    
    return totalIntensity;
  }

  // Utility function to convert degrees to radians
  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  // Mock data generators — used only as a fallback for the presentation
  // build of GIS Analytics (the real /gis/heatmap endpoint requires a
  // center point this page never sends, so this is what actually renders).
  // Addresses are real uMngeni/Howick-area locations so the demo reads as
  // grounded in the actual municipality rather than generic coordinates
  // (the previous version centered on Durban, a different city entirely).
  private static readonly HOWICK_LOCATIONS: Array<{ lat: number; lng: number; address: string }> = [
    { lat: -29.4858, lng: 30.2306, address: 'Main Street, Howick CBD' },
    { lat: -29.4872, lng: 30.2289, address: 'Karkloof Road, Howick' },
    { lat: -29.4845, lng: 30.2321, address: 'Old Main Road, Howick' },
    { lat: -29.4861, lng: 30.2298, address: 'Bosman Street, Howick' },
    { lat: -29.4839, lng: 30.2315, address: 'Silver Falls Road, Howick' },
    { lat: -29.4880, lng: 30.2275, address: 'Loop Street, Howick West' },
    { lat: -29.5021, lng: 30.2489, address: 'Cascades Road, Merrivale' },
    { lat: -29.4967, lng: 30.2612, address: 'Hilton Road, Merrivale' },
    { lat: -29.4703, lng: 30.2158, address: 'Trewirgie Road, Howick' },
    { lat: -29.4791, lng: 30.2402, address: 'Shafton Road, KwaMevana' },
    { lat: -29.4756, lng: 30.2358, address: 'Amber Valley Road, KwaMevana' },
    { lat: -29.5108, lng: 30.2233, address: 'Blackburrow Road, Howick West' },
  ];

  private generateMockHeatmapData(filters: GISFilters, departments: Array<{ id: string; name: string }> = []): HeatmapPoint[] {
    const eventTypes: HeatmapPoint['type'][] = ['report', 'assignment', 'escalation', 'response_time'];
    const deptPool = departments.length > 0 ? departments : [{ id: 'mock_dept', name: 'General' }];

    const mockData: HeatmapPoint[] = [];
    for (let i = 0; i < 50; i++) {
      const loc = GISService.HOWICK_LOCATIONS[i % GISService.HOWICK_LOCATIONS.length];
      const jitter = () => (Math.random() - 0.5) * 0.004; // small scatter around the named location
      const dept = deptPool[Math.floor(Math.random() * deptPool.length)];
      const type = eventTypes[Math.floor(Math.random() * eventTypes.length)];

      mockData.push({
        id: `mock_${i}`,
        latitude: loc.lat + jitter(),
        longitude: loc.lng + jitter(),
        address: loc.address,
        intensity: Math.random() * 100,
        type,
        timestamp: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
        metadata: {
          departmentId: dept.id,
          departmentName: dept.name,
          priority: ['low', 'medium', 'high'][Math.floor(Math.random() * 3)],
          responseTime: Math.random() * 60,
        },
      });
    }

    // Filters are applied here rather than server-side since this whole
    // path only runs when the real endpoint is unavailable — the
    // department/type dropdowns need to visibly do something for a demo.
    return mockData.filter((point) => {
      if (filters.departmentId && point.metadata.departmentId !== filters.departmentId) return false;
      if (filters.eventType && point.type !== filters.eventType) return false;
      return true;
    });
  }

  private generateMockClusteredData(filters: GISFilters): ClusteredPoint[] {
    const mockData: ClusteredPoint[] = [];
    const centerLat = -29.8587;
    const centerLng = 31.0218;
    
    for (let i = 0; i < 10; i++) {
      const lat = centerLat + (Math.random() - 0.5) * 0.1;
      const lng = centerLng + (Math.random() - 0.5) * 0.1;
      
      mockData.push({
        id: `cluster_${i}`,
        latitude: lat,
        longitude: lng,
        pointCount: Math.floor(Math.random() * 20) + 1,
        pointIds: Array.from({ length: Math.floor(Math.random() * 20) + 1 }, (_, j) => `point_${i}_${j}`),
        departmentId: filters.departmentId || 'mock_dept',
        averageResponseTime: Math.random() * 60,
        averagePriority: Math.random() * 5,
        types: ['report', 'assignment'],
      });
    }
    
    return mockData;
  }

  private generateMockServiceAreas(filters: GISFilters): ServiceArea[] {
    const mockAreas: ServiceArea[] = [];
    const centerLat = -29.8587;
    const centerLng = 31.0218;
    
    const departments = ['Electricity', 'Water', 'Roads', 'Waste'];
    
    for (let i = 0; i < departments.length; i++) {
      const lat = centerLat + (Math.random() - 0.5) * 0.05;
      const lng = centerLng + (Math.random() - 0.5) * 0.05;
      
      // Generate a simple polygon (square for mock data)
      const boundary = [
        { latitude: lat - 0.01, longitude: lng - 0.01 },
        { latitude: lat - 0.01, longitude: lng + 0.01 },
        { latitude: lat + 0.01, longitude: lng + 0.01 },
        { latitude: lat + 0.01, longitude: lng - 0.01 },
      ];
      
      mockAreas.push({
        id: `area_${i}`,
        name: `${departments[i]} Service Area`,
        departmentId: `dept_${i}`,
        departmentName: departments[i],
        boundary,
        areaKm2: Math.random() * 100 + 50,
        technicianCount: Math.floor(Math.random() * 10) + 1,
        averageResponseTime: Math.random() * 60 + 15,
        totalReports: Math.floor(Math.random() * 100) + 10,
        averagePriority: Math.random() * 5 + 1,
      });
    }
    
    return mockAreas;
  }
}

export const gisService = new GISService();
export default gisService;





